import React, { useContext, useCallback, useMemo, useState } from 'react';
import { compose } from 'recompose';
import { useNavigate } from 'react-router';
import { css } from '@emotion/react';
import { TextField, Grid, InputAdornment, MenuItem, LinearProgress } from '@material-ui/core';
import { OnwardTab, OnwardTabContainer } from '@/components/Tabs';
import withAdminRights from '@/components/Auth/withAdminRights';
import { UserContext } from '@/components/App';
import { useOrderTableProfiles } from '@/hooks';

import ExceptionResolutionModal from './modals/ExceptionResolutionModal';
import CustomizableTable from '@/components/CustomizableTable';
import CustomizableTableProfileControl from '@/components/CustomizableTable/CustomizableTableProfileControl';
import AdminExceptionsColumnFilterPanel, { COLUMN_FILTER_MAP } from './ColumnFilterPanel';
import { Context, ContextProvider } from './store';
import { TABS } from './constants';
import { H1 } from './blocks';
import { PrimaryButton } from '@/styles/blocks';
import SearchIcon from '@material-ui/icons/Search';

const DEFAULT_TABLE_INITIAL_STATE = {
    hiddenColumns: [],
    columnOrder: [],
    columnResizing: { columnWidths: {} },
};

const EXCEPTION_PROFILE_FILTER_KEYS = ['start', 'end', 'type', 'occurred_at', 'shippers', 'carriers', 'searchTerm'];

export const AdminExceptions = ({ context }) => {
    const navigate = useNavigate();
    const { user } = useContext(UserContext);
    const { state, loading, callbacks } = useContext(context);

    const [tableInitialState, setTableInitialState] = useState(DEFAULT_TABLE_INITIAL_STATE);
    const [tableKey, setTableKey] = useState(0);
    const [tableLayoutState, setTableLayoutState] = useState({
        hiddenColumns: [],
        columnOrder: [],
        columnWidths: {},
    });
    const [openColumnFilter, setOpenColumnFilter] = useState(null);

    const applyFilters = useCallback(
        (updates) => {
            callbacks.setFilter((prev) => ({ ...prev, ...updates }));
        },
        [callbacks]
    );

    const buildProfileSettings = useCallback(() => {
        const filterValues = {};
        EXCEPTION_PROFILE_FILTER_KEYS.forEach((key) => {
            const val = state.filter[key];
            if (val !== null && val !== undefined) {
                if (Array.isArray(val)) {
                    filterValues[key] = val.length ? val : null;
                } else if (val instanceof Date) {
                    filterValues[key] = val.toISOString();
                } else {
                    filterValues[key] = val === '' ? null : val;
                }
            }
        });

        return {
            layout: {
                hiddenColumns: tableLayoutState.hiddenColumns || [],
                columnOrder: tableLayoutState.columnOrder || [],
                columnWidths: tableLayoutState.columnWidths || {},
            },
            sorting: {
                sortBy: state.sortBy?.id ? [{ id: state.sortBy.id, desc: !!state.sortBy.desc }] : [],
            },
            filters: {
                values: filterValues,
            },
        };
    }, [state.filter, state.sortBy, tableLayoutState]);

    const applyProfileSettings = useCallback(
        (settings) => {
            const layout = settings?.layout || {};
            setTableInitialState({
                hiddenColumns: layout.hiddenColumns || DEFAULT_TABLE_INITIAL_STATE.hiddenColumns,
                columnOrder: layout.columnOrder || DEFAULT_TABLE_INITIAL_STATE.columnOrder,
                columnResizing: { columnWidths: layout.columnWidths || {} },
            });
            setTableKey((k) => k + 1);

            const nextSort = settings?.sorting?.sortBy?.[0];
            if (nextSort?.id) {
                callbacks.setSortBy?.({ id: nextSort.id, desc: !!nextSort.desc });
            } else {
                callbacks.setSortBy?.({ id: 'exception_time', desc: true });
            }

            const savedValues = settings?.filters?.values || {};
            const nextFilter = { ...state.filter };
            EXCEPTION_PROFILE_FILTER_KEYS.forEach((key) => {
                const val = savedValues[key];
                if (val !== undefined) {
                    if (key === 'start' || key === 'end') {
                        nextFilter[key] = val ? new Date(val) : null;
                    } else if (key === 'shippers' || key === 'carriers') {
                        nextFilter[key] = Array.isArray(val) ? val : [];
                    } else {
                        nextFilter[key] = val ?? (key === 'searchTerm' ? '' : null);
                    }
                }
            });
            callbacks.setFilter(nextFilter);
        },
        [callbacks, state.filter]
    );

    const resetToDefaults = useCallback(() => {
        setTableInitialState(DEFAULT_TABLE_INITIAL_STATE);
        setTableKey((k) => k + 1);
        callbacks.setSortBy?.({ id: 'exception_time', desc: true });
        callbacks.setFilter((prev) => ({
            ...prev,
            start: null,
            end: null,
            type: null,
            occurred_at: null,
            shippers: [],
            carriers: [],
            searchTerm: '',
        }));
    }, [callbacks]);

    const {
        profiles,
        profilesLoading,
        selectedProfileId,
        currentProfileName,
        currentProfileId,
        profileSelectProps,
        handleSaveNewProfile,
        handleOverwriteProfile,
        handleDeleteCurrentProfile,
        handleImportProfileById,
        insertProfileLoading,
        updateProfileLoading,
        deleteProfileLoading,
    } = useOrderTableProfiles({
        tableType: state.tableType || 'ADMIN_EXCEPTIONS',
        userId: user?.user_id,
        buildProfileSettings,
        applyProfileSettings,
        onResetToDefault: resetToDefaults,
        notifyError: (message) => {},
    });

    const handleOpenColumnFilter = useCallback(
        (columnId) => {
            if (state.filterableColumnIds?.includes(columnId)) {
                setOpenColumnFilter(columnId);
            }
        },
        [state.filterableColumnIds]
    );

    const handleClearColumnFilter = useCallback(
        (columnId) => {
            const config = COLUMN_FILTER_MAP[columnId];
            if (!config?.controls?.length) return;

            const resetValues = {};
            config.controls.forEach((control) => {
                if (control.type === 'dateRange' && control.keys) {
                    control.keys.forEach((k) => {
                        resetValues[k] = null;
                    });
                } else if (control.key) {
                    resetValues[control.key] = control.type === 'multiSelect' ? [] : null;
                }
            });

            if (Object.keys(resetValues).length) {
                applyFilters(resetValues);
            }

            if (openColumnFilter === columnId) {
                setOpenColumnFilter(null);
            }
        },
        [applyFilters, openColumnFilter]
    );

    const tableCallbacks = useMemo(
        () => ({
            ...callbacks,
            gotoDetails: (order) => navigate(`/order/${order?.order_id}`),
        }),
        [callbacks, navigate]
    );

    const memoizedSortBy = useMemo(() => (state.sortBy ? [state.sortBy] : []), [state.sortBy]);

    return (
        <>
            <AdminExceptionsColumnFilterPanel
                open={Boolean(openColumnFilter)}
                columnId={openColumnFilter}
                onClose={() => setOpenColumnFilter(null)}
                applyFilters={applyFilters}
                filter={state.filter}
                shippers={state.shippers || []}
                carriers={state.carriers || []}
            />
            <Grid
                direction="column"
                container
                css={css`
                    width: 100%;
                    max-width: 100%;
                    height: 100%;
                    min-width: 0;
                    overflow-x: hidden;
                `}
            >
                <Grid
                    direction="row"
                    item
                    css={css`
                        display: flex;
                        background-color: white;
                    `}
                >
                    <Grid
                        direction="column"
                        container
                        css={css`
                            flex: 1;
                            flex-basis: 0;
                            align-content: center;
                        `}
                    >
                        <OnwardTabContainer
                            value={state.filter.status}
                            onChange={(e, t) => {
                                callbacks.setFilter((prev) => ({ ...prev, status: t }));
                            }}
                            textColor="primary"
                            indicatorColor="primary"
                            centered
                        >
                            {TABS.map((tab) => (
                                <OnwardTab key={tab.value} label={tab.label} value={tab.value} />
                            ))}
                        </OnwardTabContainer>
                    </Grid>
                    <Grid
                        direction="column"
                        container
                        css={css`
                            flex: 0;
                            flex-basis: 0;
                            justify-content: center;
                            padding: 0 16px;
                        `}
                    >
                        <PrimaryButton loading={loading.export} onClick={callbacks.exportCSV}>
                            Export
                        </PrimaryButton>
                    </Grid>
                </Grid>
                <Grid
                    container
                    item
                    direction="row"
                    css={css`
                        padding: 40px;
                        padding-bottom: 0;
                        margin-bottom: 12px;
                    `}
                >
                    <H1>Exception Manager</H1>
                </Grid>
                <Grid
                    container
                    item
                    direction="row"
                    css={css`
                        flex-wrap: nowrap;
                        padding: 0 40px;
                        padding-bottom: 0;
                        margin-bottom: 12px;
                        align-items: center;
                        gap: 16px;
                    `}
                >
                    <Grid
                        container
                        direction="column"
                        css={css`
                            flex: 1;
                            flex-basis: 0;
                            overflow: hidden;
                            max-width: 300px;
                        `}
                    >
                        <TextField
                            variant="outlined"
                            fullWidth
                            label="Search"
                            placeholder="Search..."
                            value={state?.filter?.searchTerm || ''}
                            onChange={(e) => {
                                callbacks.setFilter((prev) => ({
                                    ...prev,
                                    searchTerm: e.target.value,
                                }));
                            }}
                            InputProps={{
                                startAdornment: (
                                    <InputAdornment position="start">
                                        <SearchIcon />
                                    </InputAdornment>
                                ),
                            }}
                            css={css`
                                background-color: white;
                                width: 100%;
                                margin-top: 0.2rem;
                            `}
                        />
                    </Grid>
                    <TextField
                        select
                        variant="outlined"
                        size="small"
                        label={profiles?.length ? 'Profile' : 'Select Profile...'}
                        InputLabelProps={{ shrink: true }}
                        {...profileSelectProps}
                        InputProps={{ style: { backgroundColor: 'white', minWidth: 180 } }}
                    >
                        <MenuItem value="">Default</MenuItem>
                        {profiles?.map((p) => (
                            <MenuItem key={p.order_table_profile_id} value={p.order_table_profile_id}>
                                {p.name}
                            </MenuItem>
                        ))}
                    </TextField>
                    <CustomizableTableProfileControl
                        disabled={profilesLoading}
                        tableType={state.tableType || 'ADMIN_EXCEPTIONS'}
                        currentProfileName={currentProfileName}
                        currentProfileId={currentProfileId}
                        selectedProfileId={selectedProfileId}
                        onSaveNew={handleSaveNewProfile}
                        onOverwrite={handleOverwriteProfile}
                        onImportById={handleImportProfileById}
                        onDeleteCurrent={handleDeleteCurrentProfile}
                        saving={insertProfileLoading}
                        overwriting={updateProfileLoading}
                        deleting={deleteProfileLoading}
                    />
                </Grid>

                {loading.init && (
                    <Grid
                        item
                        css={css`
                            padding: 0 40px;
                        `}
                    >
                        <LinearProgress
                            color="primary"
                            css={css`
                                width: 100%;
                            `}
                        />
                    </Grid>
                )}

                <Grid
                    item
                    css={css`
                        overflow: hidden;
                        flex: 1 1 0;
                        min-height: 0;
                        min-width: 0;
                        max-width: 100%;
                        padding: 0 40px;
                    `}
                >
                    <CustomizableTable
                        key={tableKey}
                        data={state.exceptions}
                        columns={state.columns}
                        enableRowSelection={true}
                        selectedRowIds={state.selected || {}}
                        onSelectionChange={callbacks.selectExceptions}
                        sortBy={memoizedSortBy}
                        onSortChange={(newSortBy) => {
                            const next = newSortBy?.id ? newSortBy : { id: 'exception_time', desc: true };
                            if (next.id !== state.sortBy?.id || next.desc !== state.sortBy?.desc) {
                                callbacks.setSortBy?.(next);
                            }
                        }}
                        onLoadMore={callbacks.loadMore}
                        hasMore={state.hasMore}
                        isLoading={loading.init}
                        getRowId={(row) => row.mapping_id}
                        onRowClick={(row) => callbacks.resolveException({ original: row })}
                        initialState={tableInitialState}
                        onLayoutChange={setTableLayoutState}
                        wrapBodyCellContent={true}
                        manualSortBy={true}
                        filterableColumns={state.filterableColumnIds || []}
                        onOpenColumnFilter={handleOpenColumnFilter}
                        onClearColumnFilter={handleClearColumnFilter}
                        cellRenderContext={{
                            callbacks: tableCallbacks,
                        }}
                    />
                </Grid>
            </Grid>
            <ExceptionResolutionModal
                {...state.toResolve}
                loading={loading.resolve}
                callbacks={{
                    onClose: () => {
                        callbacks.closeModal({});
                    },
                    onResolve: ({
                        toSubmit,
                        items = [],
                        order = {},
                        exception = {},
                        event = {},
                        isWhReturn = false,
                    }) => {
                        const toRemove = items.map((item) => item.item_id);
                        const { order_id, ...orderUpdates } = order;
                        const { event_id, ...eventUpdates } = event;
                        const { exception_id, ...exceptionUpdates } = exception;

                        return Promise.all([
                            callbacks.resolve({
                                variables: {
                                    orders: order_id ? [order_id] : [],
                                    events: event_id ? [event_id] : [],
                                    exceptions: exception_id ? [exception_id] : [],
                                    items: toRemove,
                                    order_update: orderUpdates,
                                    event_update: eventUpdates,
                                    exception_update: exceptionUpdates,
                                    include_route_update: false,
                                },
                            }),
                            ...(toSubmit
                                ? [
                                      callbacks.submitOrders({
                                          client_id: toSubmit.shipper_id,
                                          orders: [toSubmit],
                                          filename: undefined,
                                          type: 'MANUAL',
                                      }),
                                  ]
                                : []),
                        ]).then(async ([, resp]) => {
                            if (resp?.data?.jobs?.length > 0) {
                                if (exception.resolution === 'CREATE_RETURN') {
                                    await callbacks.saveAssociatedReturnOrder(order_id, resp.data.jobs[0], isWhReturn);
                                }
                                navigate(`/job/${resp.data.jobs[0]}`);
                            } else {
                                callbacks.closeModal({});
                            }
                        });
                    },
                }}
            />
        </>
    );
};

const withContext = (Component) => (props) =>
    (
        <ContextProvider>
            <Component {...props} context={Context} />
        </ContextProvider>
    );

export default compose(withAdminRights, withContext)(AdminExceptions);
